import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Car, Waves, Building2, Sparkles, Check, Phone, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CALC_SERVICES = [
  { key: 'garage', label: 'Garage Floor Coating', icon: Car, low: 6, high: 10,
    presets: [{ name: '1-Car Garage', sqft: 250 }, { name: '2-Car Garage', sqft: 450 }, { name: '3-Car Garage', sqft: 650 }] },
  { key: 'metallic', label: 'Metallic Epoxy', icon: Sparkles, low: 8, high: 15,
    presets: [{ name: '1-Car Garage', sqft: 250 }, { name: '2-Car Garage', sqft: 450 }, { name: 'Interior Space', sqft: 800 }] },
  { key: 'pool', label: 'Pool Deck / Lanai', icon: Waves, low: 8, high: 12,
    presets: [{ name: 'Small Lanai', sqft: 300 }, { name: 'Medium Lanai', sqft: 500 }, { name: 'Large Pool Deck', sqft: 800 }] },
  { key: 'commercial', label: 'Commercial Floors', icon: Building2, low: 4, high: 8,
    presets: [{ name: 'Small Shop', sqft: 1000 }, { name: 'Retail / Office', sqft: 2500 }, { name: 'Warehouse', sqft: 5000 }] },
];

export const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

const CostCalculatorWidget = ({ sourcePage = '/cost-calculator' }) => {
  const [service, setService] = useState(CALC_SERVICES[0]);
  const [sqft, setSqft] = useState(450);
  const [lead, setLead] = useState({ name: '', email: '', phone: '', city: '', website: '' });
  const [status, setStatus] = useState('idle');

  const validSqft = Number(sqft) > 0 ? Number(sqft) : 0;
  const lowEst = validSqft * service.low;
  const highEst = validSqft * service.high;

  const submitLead = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          service: service.key,
          location: lead.city || null,
          sqft: `${validSqft} sq ft (calculator)`,
          source_page: sourcePage,
          website: lead.website,
          message: `Cost Calculator lead — ${service.label}, ${validSqft} sq ft, on-screen estimate ${fmt(lowEst)}–${fmt(highEst)}. Requesting exact quote.`,
        }),
      });
      if (!res.ok) throw new Error('failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="cost-calculator">
      <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-4">1. What are we coating?</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        {CALC_SERVICES.map((s) => {
          const Icon = s.icon;
          const active = s.key === service.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => { setService(s); setSqft(s.presets[1].sqft); }}
              data-testid={`calc-service-${s.key}`}
              className={`rounded-xl border-2 p-4 text-center transition-colors ${active ? 'border-brand-gold bg-brand-gold/10' : 'border-gray-200 bg-white hover:border-brand-gold/50'}`}
            >
              <Icon className={`w-7 h-7 mx-auto mb-2 ${active ? 'text-brand-gold-dark' : 'text-gray-400'}`} />
              <div className="font-bold text-sm text-gray-900">{s.label}</div>
              <div className="text-xs text-gray-500 mt-1">${s.low}–${s.high}/sq ft</div>
            </button>
          );
        })}
      </div>

      <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-4">2. How big is the area?</h2>
      <div className="flex flex-wrap gap-3 mb-4">
        {service.presets.map((p) => (
          <button
            key={p.name}
            type="button"
            onClick={() => setSqft(p.sqft)}
            data-testid={`calc-preset-${p.sqft}`}
            className={`px-4 py-2 rounded-full border-2 text-sm font-semibold transition-colors ${Number(sqft) === p.sqft ? 'border-brand-gold bg-brand-gold text-brand-black' : 'border-gray-200 bg-white text-gray-700 hover:border-brand-gold/50'}`}
          >
            {p.name} · ~{p.sqft} sq ft
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-10">
        <Input type="number" min="1" value={sqft} onChange={(e) => setSqft(e.target.value)} className="max-w-[160px] text-lg font-bold" data-testid="calc-sqft-input" />
        <span className="text-gray-600 font-medium">square feet (approximate is fine)</span>
      </div>

      <div className="bg-brand-black text-white rounded-2xl p-8 text-center mb-10" data-testid="calc-estimate-box">
        <p className="text-brand-gold font-semibold mb-2">Your estimated range — {service.label}, {validSqft.toLocaleString()} sq ft</p>
        <p className="text-4xl sm:text-5xl font-bold mb-3" data-testid="calc-estimate-range">
          {validSqft > 0 ? `${fmt(lowEst)} – ${fmt(highEst)}` : 'Enter a size above'}
        </p>
        <p className="text-gray-300 text-sm max-w-xl mx-auto">
          Real installed price depends on concrete condition, repairs and finish options. Leave your info below and we confirm your exact number within 24 hours — no site visit needed, photos are enough.
        </p>
      </div>

      {status === 'success' ? (
        <div className="bg-green-50 border-2 border-green-500 rounded-2xl p-8 text-center" data-testid="calc-success-message">
          <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-2">Estimate request received!</h2>
          <p className="text-gray-700 mb-4">We'll confirm your exact price for {validSqft.toLocaleString()} sq ft of {service.label.toLowerCase()} within 24 hours.</p>
          <a href="tel:239-276-1462"><Button className="bg-brand-gold hover:bg-brand-gold-dark text-brand-black font-bold"><Phone className="w-4 h-4 mr-2" />Or call now: (239) 276-1462</Button></a>
        </div>
      ) : (
        <form onSubmit={submitLead} className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8" data-testid="calc-lead-form">
          <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-1">3. Get your exact quote — free</h2>
          <p className="text-sm text-gray-600 mb-5">We reply within 24 hours with a firm number. No obligation, no pushy sales.</p>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <Input required placeholder="Your name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="calc-name-input" />
            <Input required type="email" placeholder="Email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="calc-email-input" />
            <Input required type="tel" placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="calc-phone-input" />
            <Input placeholder="City (Naples, Fort Myers...)" value={lead.city} onChange={(e) => setLead({ ...lead, city: e.target.value })} data-testid="calc-city-input" />
          </div>
          <input type="text" name="website" value={lead.website} onChange={(e) => setLead({ ...lead, website: e.target.value })} className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" />
          <Button type="submit" disabled={status === 'sending'} size="lg" className="w-full bg-brand-gold hover:bg-brand-gold-dark text-brand-black font-bold text-lg" data-testid="calc-submit-btn">
            {status === 'sending' ? 'Sending...' : `Lock In My Exact Quote (${validSqft > 0 ? `${fmt(lowEst)}–${fmt(highEst)}` : 'free'})`}
          </Button>
          {status === 'error' && <p className="text-red-600 text-sm mt-3 text-center" data-testid="calc-error-message">Something went wrong — please try again or call (239) 276-1462.</p>}
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
            <ShieldCheck className="w-4 h-4 text-green-600" /> Licensed & insured · Lifetime residential warranty · 5.0 Google rating
          </div>
        </form>
      )}

      <p className="text-xs text-gray-500 mt-6 text-center">
        Ranges reflect typical installed prices in Southwest Florida for flake, metallic, pool deck and commercial systems — UV-stable polyaspartic topcoat included. Heavy concrete repair, moisture mitigation or old coating removal are quoted separately. Prefer photos? <Link to="/contact" className="text-brand-gold-dark font-semibold underline">Send them here</Link>.
      </p>
    </div>
  );
};

export default CostCalculatorWidget;
